import { useUiTranslation } from 'lib/i18n/context';
import {
    useLocation,
    useParams,
    Link,
    useSearchParams,
} from 'react-router-dom';
import React from 'react';
import { Skeleton } from 'components/ui/skeleton';
import { AppSidebar } from 'components/layouts/dashboard-layout/app-sidebar';
import { isUUID } from 'lib/utils';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from 'components/ui/breadcrumb';
import { Separator } from 'components/ui/separator';
import {
    SidebarInset,
    SidebarProvider,
    SidebarTrigger,
} from 'components/ui/sidebar/sidebar';
import { useProjectGroup } from 'features/project/api/get-project-group';
import { useDatasetGroup } from 'features/datasets/api/get-dataset-group';
import { useGraderGroup } from 'features/graders/api/get-grader-group';
import { useDeploymentGroup } from 'features/deployments/api/get-deployment-group';
import { useTraceGroup } from 'features/telemetry/api/get-trace-group';
import { useToolGroup } from 'features/tools/api/get-tool-group';
import { Toaster } from 'components/ui/sonner';
import { useImplementation } from 'features/project/api/get-implementation';
import { OnboardingGuard } from 'features/legal/components/onboarding-guard';
import type { DashboardNavigation } from './types';
import { ClaimReviewLayoutContext } from './claim-review-layout-context';

const DynamicBreadcrumbs = () => {
    const { t } = useUiTranslation('common');
    const location = useLocation();
    const params = useParams();
    const pathnames = location.pathname.split('/').filter((x) => x);
    const [searchParams] = useSearchParams();

    const { data: projectGroup } = useProjectGroup({
        id: params.projectGroupId!,
        queryConfig: { enabled: !!params.projectGroupId },
    });
    const { data: datasetGroup } = useDatasetGroup({
        id: params.datasetGroupId!,
        queryConfig: { enabled: !!params.datasetGroupId },
    });
    const { data: graderGroup } = useGraderGroup({
        id: params.graderGroupId!,
        queryConfig: { enabled: !!params.graderGroupId },
    });
    const { data: deploymentGroup } = useDeploymentGroup({
        deploymentGroupId: params.deploymentGroupId!,
        queryConfig: { enabled: !!params.deploymentGroupId },
    });
    const { data: traceGroup } = useTraceGroup(params.traceGroupId!, {
        queryConfig: { enabled: !!params.traceGroupId },
    });
    const { data: implementation } = useImplementation({
        implementationId: params.implementationId!,
        queryConfig: { enabled: !!params.implementationId },
    });
    const { data: toolGroup } = useToolGroup({
        id: params.toolGroupId!,
        queryConfig: { enabled: !!params.toolGroupId },
    });

    return (
        <Breadcrumb>
            <BreadcrumbList>
                <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                        <Link to="/app">{t(($) => $.home)}</Link>
                    </BreadcrumbLink>
                </BreadcrumbItem>
                {pathnames.map((name, index) => {
                    if (name === 'app') return null;
                    const routeTo = `/${pathnames.slice(0, index + 1).join('/')}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
                    const isLast = index === pathnames.length - 1;

                    let displayName: React.ReactNode = [
                        'claims',
                        'new',
                        'settings',
                    ].includes(name)
                        ? t(
                              ($) =>
                                  $.route_names[
                                      name as 'claims' | 'new' | 'settings'
                                  ],
                          )
                        : name;
                    if (index > 0) {
                        const prevSegment = pathnames[index - 1];
                        if (
                            prevSegment === 'projects' &&
                            isUUID(params.projectGroupId!)
                        ) {
                            if (projectGroup) {
                                displayName = projectGroup.name;
                            } else {
                                displayName = <Skeleton className="h-4 w-24" />;
                            }
                        } else if (
                            prevSegment === 'datasets' &&
                            isUUID(params.datasetGroupId!)
                        ) {
                            if (datasetGroup) {
                                displayName = datasetGroup.name;
                            } else {
                                displayName = <Skeleton className="h-4 w-24" />;
                            }
                        } else if (
                            prevSegment === 'graders' &&
                            isUUID(params.graderGroupId!)
                        ) {
                            if (graderGroup) {
                                displayName = graderGroup.name;
                            } else {
                                displayName = <Skeleton className="h-4 w-24" />;
                            }
                        } else if (
                            prevSegment === 'deployments' &&
                            isUUID(params.deploymentGroupId!)
                        ) {
                            if (deploymentGroup) {
                                displayName = deploymentGroup.name;
                            } else {
                                displayName = <Skeleton className="h-4 w-24" />;
                            }
                        } else if (
                            prevSegment === 'trace-groups' &&
                            isUUID(params.traceGroupId!)
                        ) {
                            if (traceGroup) {
                                displayName = traceGroup.name;
                            } else {
                                displayName = <Skeleton className="h-4 w-24" />;
                            }
                        } else if (
                            prevSegment === 'implementations' &&
                            isUUID(params.implementationId!)
                        ) {
                            if (implementation) {
                                displayName = implementation.name;
                            } else {
                                displayName = <Skeleton className="h-4 w-24" />;
                            }
                        } else if (
                            prevSegment === 'tools' &&
                            isUUID(params.toolGroupId!)
                        ) {
                            displayName = toolGroup?.name ?? 'Tools';
                        }
                    }

                    return (
                        <React.Fragment key={routeTo}>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                {isLast ? (
                                    <BreadcrumbPage className="max-w-40 truncate capitalize">
                                        {displayName}
                                    </BreadcrumbPage>
                                ) : (
                                    <BreadcrumbLink
                                        asChild
                                        className="max-w-40 truncate capitalize"
                                    >
                                        <Link to={routeTo}>{displayName}</Link>
                                    </BreadcrumbLink>
                                )}
                            </BreadcrumbItem>
                        </React.Fragment>
                    );
                })}
            </BreadcrumbList>
        </Breadcrumb>
    );
};

interface DashboardLayoutProps {
    children: React.ReactNode;
    navigation: DashboardNavigation;
    claimReview?: boolean;
}

export const DashboardLayout = ({
    children,
    navigation,
    claimReview = false,
}: DashboardLayoutProps) => {
    const [detailsExpanded, setDetailsExpanded] = React.useState(false);
    const [reviewNavigationOpen, setReviewNavigationOpen] =
        React.useState(false);
    const layoutId = React.useId();
    const appHeaderId = `${layoutId}-app-header`;
    const claimDetailsId = `${layoutId}-claim-details`;
    const navigationId = `${layoutId}-navigation`;
    return (
        <SidebarProvider
            open={claimReview ? reviewNavigationOpen : undefined}
            onOpenChange={claimReview ? setReviewNavigationOpen : undefined}
        >
            <AppSidebar navigation={navigation} id={navigationId} />
            <SidebarInset>
                <header
                    id={appHeaderId}
                    hidden={claimReview && !detailsExpanded}
                    className={
                        claimReview
                            ? 'h-10 shrink-0 items-center gap-2 [&:not([hidden])]:flex'
                            : 'flex h-16 shrink-0 items-center gap-2'
                    }
                >
                    <div className="flex items-center gap-2 px-4">
                        {!claimReview && (
                            <>
                                <SidebarTrigger className="-ml-1" />
                                <Separator
                                    orientation="vertical"
                                    className="mr-2 h-4"
                                />
                            </>
                        )}
                        <DynamicBreadcrumbs />
                    </div>
                </header>
                <div
                    className={
                        claimReview
                            ? 'flex min-w-0 flex-1 flex-col gap-2 p-1.5 sm:p-2 2xl:p-3'
                            : 'flex flex-1 flex-col gap-4 p-4 pt-0'
                    }
                >
                    <ClaimReviewLayoutContext.Provider
                        value={
                            claimReview
                                ? {
                                      detailsExpanded,
                                      setDetailsExpanded,
                                      appHeaderId,
                                      claimDetailsId,
                                      navigationId,
                                  }
                                : null
                        }
                    >
                        <OnboardingGuard>{children}</OnboardingGuard>
                    </ClaimReviewLayoutContext.Provider>
                    <Toaster className="pointer-events-auto" />
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
};
